import React, {createContext, use, useCallback, useEffect, useRef, useState} from "react";
import PropTypes from "prop-types";
import {useLocation, useNavigate} from "react-router-dom";
import {useOrgSettings} from "./OrgSettingsProvider";
import {usePermissions} from "./AuthzProvider";
import {PLAYBOOK_WIDGET_TOOL_NAME} from "../components/agent/useAgentListTools/toolNames";
import {submitPlaybookWidget} from "../components/chatbot/widgets/PlaybookSelectorWidget";
import {filter} from "underscore";

const RexAIContext = createContext(null);

const RexAIProvider = ({children}) => {
  const {aiPermissions} = useOrgSettings();
  const {canAccessTrustedAdvisor} = usePermissions();
  const navigate = useNavigate();
  const {pathname} = useLocation();
  const [cooldownLeft, setCooldownLeft] = useState(0);
  const [sessionId, setSessionIdState] = useState(null);
  const [messageContext, setMessageContext] = useState(null);
  const [scopeContext, setScopeContext] = useState(null);
  const [agentSupportedOnPage, setAgentSupportedOnPage] = useState(false);
  const [agentPanelState, setAgentPanelState] = useState("closed");
  const [onAgentWrite, setOnAgentWrite] = useState(null);
  const [agentUiCompressed, setAgentUiCompressed] = useState(false);
  const [formToolsEnabled, setFormToolsEnabled] = useState(false);
  const [allowedUiFormFields, setAllowedUiFormFields] = useState([]);
  const [allowedListTools, setAllowedListTools] = useState([]);
  const [addFormLabel, setAddFormLabel] = useState(null);
  const [contextAccess, setContextAccess] = useState(null);
  const [contextCaption, setContextCaption] = useState(null);
  const [contextTooltip, setContextTooltip] = useState(null);
  const [pendingClientTool, setPendingClientToolState] = useState(null);
  const [pendingAutoSend, setPendingAutoSend] = useState(null);
  const [pendingAddPlaybook, setPendingAddPlaybook] = useState(null);
  const [pendingWidgetMessage, setPendingWidgetMessage] = useState(null);
  const [agentSidebarWasExpanded, setAgentSidebarWasExpanded] = useState(false);
  const [availableTabs, setAvailableTabs] = useState(null);
  const sessionIdRef = useRef(sessionId);
  const activeAgentPageRef = useRef(null);
  const pendingPageReadyRequestsRef = useRef([]);
  const pendingPageCleanupRef = useRef(null);
  const pendingClientToolRef = useRef(null);
  const activeAgentTabsRef = useRef(null);
  const shouldShowMaestro = !!aiPermissions?.trustedAdvisor && canAccessTrustedAdvisor;
  const setSessionId = useCallback((nextSessionId) => {
    if (sessionIdRef.current === nextSessionId) {
      return;
    }
    sessionIdRef.current = nextSessionId;
    setSessionIdState(nextSessionId);
    setPendingAddPlaybook(null);
    setPendingWidgetMessage(null);
  }, []);

  const handleWidgetSubmit = useCallback(
    (value, widgetSpec, label) => {
      if (!widgetSpec) {
        return;
      }
      const tools = {
        payload: widgetSpec.payload,
        navigate,
        pathname,
        setPendingAddPlaybook,
        setPendingWidgetMessage,
      };
      if (widgetSpec.type === PLAYBOOK_WIDGET_TOOL_NAME) {
        submitPlaybookWidget(
          value,
          label,
          tools,
        );
      }
    },
    [navigate, pathname],
  );

  useEffect(() => {
    if (!pendingWidgetMessage?.awaitRoute) {
      return;
    }
    if (
      pathname === pendingWidgetMessage.sourceRoute ||
      pathname === pendingWidgetMessage.awaitRoute
    ) {
      return;
    }
    setPendingWidgetMessage(null);
    setPendingAddPlaybook(null);
  }, [pathname, pendingWidgetMessage, setPendingAddPlaybook, setPendingWidgetMessage]);

  const applyAgentPage = useCallback((page) => {
    setAgentSupportedOnPage(true);
    setMessageContext({pageType: page.pageType, toolMode: page.toolMode});
    setScopeContext(page.scopeContext ?? null);
    setOnAgentWrite(() => page.onWrite ?? null);
    setFormToolsEnabled(page.allowedUiFormFields?.length > 0);
    setAllowedUiFormFields(page.allowedUiFormFields ?? []);
    setAllowedListTools(page.allowedListTools ?? []);
    setAddFormLabel(page.addFormLabel ?? null);
    setContextAccess(page.contextAccess ?? null);
    setContextCaption(page.contextCaption ?? null);
    setContextTooltip(page.contextTooltip ?? null);
  }, []);

  const waitForAgentPage = useCallback((isReady, timeoutMs = 30000) => {
    const currentPage = activeAgentPageRef.current;
    if (currentPage && isReady(currentPage)) {
      return Promise.resolve(currentPage);
    }
    return new Promise((resolve) => {
      const request = {isReady, resolve};
      request.timeout = setTimeout(() => {
        pendingPageReadyRequestsRef.current = filter(
          pendingPageReadyRequestsRef.current, (item) => item !== request,
        );
        resolve(null);
      }, timeoutMs);
      pendingPageReadyRequestsRef.current.push(request);
    });
  }, []);

  const checkPageReadyRequests = useCallback((page) => {
    pendingPageReadyRequestsRef.current = filter(pendingPageReadyRequestsRef.current, (request) => {
      if (!request.isReady(page)) {
        return true;
      }
      clearTimeout(request.timeout);
      request.resolve(page);
      return false;
    });
  }, []);

  const setPendingClientTool = useCallback((value) => {
    const nextValue = typeof value === "function" ? value(pendingClientToolRef.current) : value;
    const scopedValue =
      nextValue && !nextValue.pageRegistrationId ?
        {
          ...nextValue,
          pageRegistrationId: activeAgentPageRef.current?.registrationId,
        } :
        nextValue;
    pendingClientToolRef.current = scopedValue;
    setPendingClientToolState(scopedValue);
  }, []);

  const claimPendingClientTool = useCallback((toolUseId) => {
    const pendingTool = pendingClientToolRef.current;
    if (
      !pendingTool ||
      pendingTool.toolUseId !== toolUseId ||
      pendingTool.claimed ||
      pendingTool.pageRegistrationId !== activeAgentPageRef.current?.registrationId
    ) {
      return false;
    }

    const claimedTool = {...pendingTool, claimed: true};
    pendingClientToolRef.current = claimedTool;
    setPendingClientToolState(claimedTool);
    return true;
  }, []);

  const completePendingClientTool = useCallback((toolUseId) => {
    if (pendingClientToolRef.current?.toolUseId !== toolUseId) {
      return;
    }
    pendingClientToolRef.current = null;
    setPendingClientToolState(null);
  }, []);

  const abandonPendingClientTool = useCallback((nextRegistrationId) => {
    const pendingTool = pendingClientToolRef.current;
    if (
      !pendingTool ||
      pendingTool.claimed ||
      pendingTool.pageRegistrationId === nextRegistrationId
    ) {
      return;
    }

    pendingClientToolRef.current = null;
    setPendingClientToolState(null);
    try {
      const abandonResult = pendingTool.onAbandon?.();
      abandonResult?.catch?.((error) => {
        console.error("Failed to abandon client tool:", error);
      });
    } catch (error) {
      console.error("Failed to abandon client tool:", error);
    }
  }, []);

  const registerAgentPage = useCallback(
    (registrationId, page) => {
      clearTimeout(pendingPageCleanupRef.current);
      pendingPageCleanupRef.current = null;
      const nextPage = {...page, registrationId};
      abandonPendingClientTool(registrationId);
      activeAgentPageRef.current = nextPage;
      applyAgentPage(nextPage);
      checkPageReadyRequests(nextPage);
    },
    [abandonPendingClientTool, applyAgentPage, checkPageReadyRequests],
  );

  const updateAgentPage = useCallback(
    (registrationId, page) => {
      if (activeAgentPageRef.current?.registrationId !== registrationId) {
        return;
      }
      const nextPage = {...page, registrationId};
      activeAgentPageRef.current = nextPage;
      applyAgentPage(nextPage);
      checkPageReadyRequests(nextPage);
    },
    [applyAgentPage, checkPageReadyRequests],
  );

  const unregisterAgentPage = useCallback(
    (registrationId) => {
      if (activeAgentPageRef.current?.registrationId !== registrationId) {
        return;
      }
      activeAgentPageRef.current = null;
      pendingPageCleanupRef.current = setTimeout(() => {
        if (activeAgentPageRef.current) {
          return;
        }
        if (activeAgentTabsRef.current) {
          return;
        }

        setAgentSupportedOnPage(false);
        setMessageContext(null);
        setScopeContext(null);
        setAgentPanelState("closed");
        setOnAgentWrite(null);
        setAgentUiCompressed(false);
        setFormToolsEnabled(false);
        setAddFormLabel(null);
        setContextAccess(null);
        setContextCaption(null);
        setContextTooltip(null);
        abandonPendingClientTool(null);
        setAllowedUiFormFields([]);
        setAllowedListTools([]);
        pendingPageCleanupRef.current = null;
      }, 0);
    },
    [abandonPendingClientTool],
  );

  const registerAgentTabs = useCallback((registrationId, tabs) => {
    activeAgentTabsRef.current = {
      registrationId,
      tabs,
    };
    setAvailableTabs(tabs);
  }, []);

  const unregisterAgentTabs = useCallback((registrationId) => {
    if (activeAgentTabsRef.current?.registrationId !== registrationId) {
      return;
    }
    activeAgentTabsRef.current = null;
    const page = activeAgentPageRef.current;
    setFormToolsEnabled(page?.allowedUiFormFields?.length > 0);
    setAvailableTabs(null);
  }, []);

  useEffect(
    () => () => {
      clearTimeout(pendingPageCleanupRef.current);
      pendingPageReadyRequestsRef.current.forEach((request) => {
        clearTimeout(request.timeout);
        request.resolve(null);
      });
      pendingPageReadyRequestsRef.current = [];
    },
    [],
  );

  useEffect(() => {
    let interval;

    if (cooldownLeft > 0) {
      interval = setInterval(() => {
        setCooldownLeft((prev) => prev - 1);
      }, 1000);
    }

    return () => clearInterval(interval);
  }, [cooldownLeft]);

  return (
    <RexAIContext
      value={{
        cooldownLeft,
        setCooldownLeft,
        sessionId,
        setSessionId,
        shouldShowMaestro,
        currentPathname: pathname,
        messageContext,
        scopeContext,
        agentSupportedOnPage,
        agentPanelState,
        setAgentPanelState,
        onAgentWrite,
        agentUiCompressed,
        setAgentUiCompressed,
        formToolsEnabled,
        pendingClientTool,
        setPendingClientTool,
        claimPendingClientTool,
        completePendingClientTool,
        allowedUiFormFields,
        allowedListTools,
        addFormLabel,
        contextAccess,
        contextCaption,
        contextTooltip,
        availableTabs,
        setScopeContext,
        registerAgentPage,
        updateAgentPage,
        unregisterAgentPage,
        waitForAgentPage,
        registerAgentTabs,
        unregisterAgentTabs,
        pendingAutoSend,
        setPendingAutoSend,
        handleWidgetSubmit,
        pendingAddPlaybook,
        setPendingAddPlaybook,
        pendingWidgetMessage,
        setPendingWidgetMessage,
        agentSidebarWasExpanded,
        setAgentSidebarWasExpanded,
      }}
    >
      {children}
    </RexAIContext>
  );
};

RexAIProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useRexAI = () => {
  return use(RexAIContext);
};

export default RexAIProvider;
